import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChatBubbleLeftRightIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/solid";
import Navbar from "../components/Navbar";
import {
  listConversations,
  deleteConversation,
  getConversation,
} from "../api/councilApi";

const formatRelative = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Past Council conversations — search, browse, click to reopen in the
 * floating chat with the conversation already loaded.
 */
const ConversationHistory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listConversations();
      setItems(res.data?.conversations || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(
      (c) =>
        (c.title || "").toLowerCase().includes(q) ||
        (c.last_message?.content || "").toLowerCase().includes(q),
    );
  }, [items, query]);

  const onOpen = async (c) => {
    setBusy(c.id);
    try {
      const res = await getConversation(c.id);
      const messages = res.data?.messages || [];
      // Bounce the conversation into the floating ChatWidget via a
      // dedicated event so we don't have to refactor the widget into a
      // route-aware component.
      window.dispatchEvent(
        new CustomEvent("council:loadConversation", {
          detail: {
            conversation_id: c.id,
            title: c.title,
            messages,
          },
        }),
      );
    } catch {
      // best-effort
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Delete the conversation "${c.title}"?`)) return;
    try {
      await deleteConversation(c.id);
      setItems((cur) => cur.filter((x) => x.id !== c.id));
    } catch {
      // silent
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black dark:text-white">
      <Navbar />
      <div className="max-w-4xl mx-auto pt-28 px-6 pb-20">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold">Council conversations</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Every chat you've had with the Council — searchable, openable, deletable.
          </p>
        </div>

        <div className="relative mb-5">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title or last message…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="p-10 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 text-center">
            <ChatBubbleLeftRightIcon className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
            <h3 className="font-bold mb-1">
              {query ? "No matches" : "No conversations yet"}
            </h3>
            <p className="text-sm text-gray-500">
              {query
                ? "Try a different search."
                : "Open the chat in the bottom-right and ask the Council something."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((c) => (
              <div
                key={c.id}
                className="group flex items-start gap-3 p-4 rounded-xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 hover:border-indigo-500/50 transition cursor-pointer"
                onClick={() => onOpen(c)}
              >
                <div className="w-10 h-10 rounded-lg bg-indigo-500/15 text-indigo-500 flex items-center justify-center shrink-0">
                  <ChatBubbleLeftRightIcon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-bold truncate">
                      {c.title || "Untitled"}
                    </h3>
                    <span className="text-[11px] text-gray-500 shrink-0">
                      {formatRelative(c.last_message_at)}
                    </span>
                  </div>
                  {c.last_message?.content ? (
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate mt-1">
                      {c.last_message.role === "assistant"
                        ? "AI: "
                        : "You: "}
                      {c.last_message.content}
                    </p>
                  ) : null}
                  <p className="text-[10px] text-gray-400 mt-1">
                    {c.message_count} message{c.message_count === 1 ? "" : "s"}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(c);
                  }}
                  className="opacity-0 group-hover:opacity-100 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-500/10 transition"
                  title="Delete conversation"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
                {busy === c.id ? (
                  <span className="text-[10px] text-indigo-500 self-center">
                    Loading…
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        )}

        <p className="text-[11px] text-gray-400 mt-8">
          {items.length} total · Searches title and last message · Click to reopen
        </p>

        <div className="mt-6 text-center">
          <Link
            to="/portfolio"
            className="text-sm text-indigo-500 hover:underline"
          >
            ← Back to portfolio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ConversationHistory;
